import { motion } from "framer-motion";
import { X, Check } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];

const OTHERS = [
    {
        who: "Banks",
        line: "Want your deposits, not your clarity. You're a product to them.",
    },
    {
        who: "Money coaches",
        line: "Sell you mindset, mantras and motivation. Not the actual math.",
    },
    {
        who: "Financial advisers",
        line: "Only pick up the phone once you're already rich enough to matter.",
    },
];

const MINE = [
    "I run your raw numbers — before you have a fortune.",
    "Zero jargon, zero judgement, zero mindset fluff.",
    "One honest verdict: Red, Amber or Green — and the exact next move.",
];

export const Positioning = () => {
    return (
        <section
            id="why"
            data-testid="positioning-section"
            className="bg-[#070A13] text-[#FDFBF7]"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-32">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease }}
                    className="max-w-3xl mb-16"
                >
                    <p className="overline text-[#C5A059] mb-6">
                        — Why no one else does this
                    </p>
                    <h2 className="font-display text-4xl md:text-6xl tracking-tighter leading-[0.95]">
                        You were never{" "}
                        <span className="italic text-[#C5A059]">their</span>{" "}
                        customer.
                    </h2>
                    <p className="font-body text-lg text-white/60 leading-relaxed mt-6 max-w-xl">
                        While everyone else chases a career in a bank, I do the
                        opposite. I&apos;m the{" "}
                        <span className="text-[#FDFBF7] font-semibold">
                            first data analyst in the UK to work for people and
                            the businesses they build
                        </span>{" "}
                        — running the raw numbers for everyday lives and small
                        companies, not the FTSE 100.
                    </p>
                </motion.div>

                <div className="grid md:grid-cols-2 gap-6 md:gap-10">
                    {/* Them */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9, ease }}
                        className="border border-white/10 p-8 md:p-10"
                    >
                        <p className="overline text-white/40 mb-8">
                            Everyone else
                        </p>
                        <ul className="flex flex-col gap-7">
                            {OTHERS.map((o) => (
                                <li key={o.who} className="flex gap-4">
                                    <span className="mt-0.5 w-7 h-7 shrink-0 rounded-full border border-white/15 flex items-center justify-center text-white/40">
                                        <X size={15} />
                                    </span>
                                    <div>
                                        <p className="font-body font-semibold">
                                            {o.who}
                                        </p>
                                        <p className="font-body text-white/50 leading-relaxed">
                                            {o.line}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </motion.div>

                    {/* Me */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9, delay: 0.12, ease }}
                        className="relative border border-[#C5A059]/40 bg-[#C5A059]/[0.06] p-8 md:p-10 overflow-hidden"
                    >
                        <span className="absolute top-0 right-0 overline bg-[#C5A059] text-[#070A13] px-4 py-1.5">
                            The gap I fill
                        </span>
                        <p className="overline text-[#C5A059] mb-8">
                            The Raw Math
                        </p>
                        <ul className="flex flex-col gap-7">
                            {MINE.map((m) => (
                                <li key={m} className="flex gap-4">
                                    <span className="mt-0.5 w-7 h-7 shrink-0 rounded-full bg-[#C5A059] flex items-center justify-center text-[#070A13]">
                                        <Check size={15} />
                                    </span>
                                    <p className="font-body text-[#FDFBF7]/90 leading-relaxed font-medium">
                                        {m}
                                    </p>
                                </li>
                            ))}
                        </ul>
                        <p className="font-display italic text-2xl md:text-3xl mt-10 leading-tight text-[#FDFBF7]">
                            &ldquo;Everyone wants to work in the bank. I work for
                            the people the bank forgot.&rdquo;
                        </p>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
